// @flow
import type { SampleList } from "../../lib/OnlineTypes";
import type { CompanyBranch } from "../../lib/Types";
import React, { Fragment } from "react";
import { MinimalContainer } from "./Theme";
import styled, { css } from "styled-components";
import Input from "../Input";
import Button from "../Button";
import { fetchJson } from "../../lib/Fetching";
import moment from "moment";
import DataFieldFilter from "./DataFieldFilter";
import DropDown from "../DropDown";
import { fetchSamples } from "./Utils/SamplesFetch";
import MatrixInput from "../testing/MatrixInput";
import TechniqueInput from "../testing/TechniqueInput";
import ResultDetectedInput from "../sample_testing/inputs/ResultDetectedInput";
import ExceededLimitsInput from "../sample_testing/inputs/ExceededLimitsInput";
import TestingCategorySelect from "../sample_reception/TestingCategorySelect";
import DateFilter from "../common/form/DateFilter";
import CompanyFilter from "../common/form/CompanyFilter";
import CompanyBranchFilter from "../common/form/CompanyBranchFilter";
import ProtocolFilter from "./ProtocolFilter";
import OutstandingTodosInput from "../sample_testing/inputs/OutstandingTodosInput";
import WithdrawnSamplesInput from "../sample_testing/inputs/WithdrawnSamplesInput";
import RequiresNotificationAuthorisationInput from "../sample_testing/inputs/RequiresNotificationAuthorisationInput";
import ClientSuiteInput from "../products/ClientSuiteInput";
import InvoiceStatus from "../common/form/InvoiceStatus";
import SearchTemplateManager from "../common/SearchTemplateManager";
// import AttributeFilter from "./AttributeFilter";

type Props = {
  company_id: ?number,
  onSearch: (SampleList) => void,
  canFindCompany: boolean,
  clientView: ?boolean,
  showReceived: ?boolean,
  showReviewed: ?boolean,
  showContractReviewed: ?boolean,
  showCheckboxes: ?boolean,
  withUnallocatedSampleCharges: ?boolean,
  withSampleChargesOnSentInvoices: ?boolean,
  dateFilter: ?string,
  initialSampleId: ?number,
  laboratoryCompanyId: ?number,
  clearDates: ?boolean,
  fixedSearchParameters: ?object,
  runSearch: ?boolean,
};

type State = {
  company_id: ?number,
  loading: boolean,
  product_name: ?string,
  company_branch_id: ?number,
  protocol_id: ?number,
  start_date: ?Object,
  end_date: ?Object,
  startDateFocused: boolean,
  endDateFocused: boolean,
  received: string,
  matrixId: ?number,
  techniqueIds: ?array,
  found: boolean,
  exceededLimits: boolean,
  outstandingTodos: boolean,
  withdrawnSamples: boolean,
  fexpSamples: boolean,
  sample_id: null,
  clientSuiteId: ?number,
  requiresNotificationAuthorisation: boolean,
  analysis_started_more_than_24_hrs_since_time_sampled: boolean,
  analysis_started_less_than_24_hrs_since_time_sampled: boolean,
  requested_turnaround: ?number,
  hasProtocols: boolean,
};

const Container = styled(MinimalContainer)`
  padding: 20px;
  max-width: 1920px;
  margin: 0 auto;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 0 -15px;
`;

const Column = styled.div`
  flex-grow: 1;
  min-width: 350px;
  padding: 0 15px;
  margin-bottom: 10px;
`;

const FullColumn = styled.div`
  width: 100%;
  padding: 0 15px;
`;

function getUrlParameter(name) {
  name = name.replace(/[\[]/, "\\[").replace(/[\]]/, "\\]");
  var regex = new RegExp("[\\?&]" + name + "=([^&#]*)");
  var results = regex.exec(location.search);
  return results === null ? "" : decodeURIComponent(results[1].replace(/\+/g, " "));
}

export default class SampleSearch extends React.Component<Props, State> {
  static defaultProps = {
    showReceived: true,
    showReviewed: true,
    showContractReviewed: true,
    showCheckboxes: true,
    withUnallocatedSampleCharges: false,
    withInvoiceSentSampleLedgerItems: false,
    hideCompanySearch: false,
    clearDates: false,
    fixedSearchParameters: {},
    runSearch: false,
  };

  state = {
    company_id: this.props.company_id,
    loading: false,
    product_name: "",
    company_branch_id: null,
    protocol_id: null,
    start_date: this.props.clearDates
      ? null
      : moment().subtract(Number(getUrlParameter("days_ago")) || 10, "days"),
    end_date: this.props.clearDates ? null : moment(),
    dateFilter: this.props.dateFilter || "registered",
    startDateFocused: false,
    endDateFocused: false,
    received: getUrlParameter("received") || "all",
    reviewed: getUrlParameter("reviewed") || "all",
    invoice_status: getUrlParameter("invoice_status") || "all",
    contractReviewed: getUrlParameter("contract_reviewed") || "all",
    matrixId: null,
    techniqueIds: [],
    found: false,
    exceededLimits: false,
    outstandingTodos: false,
    withdrawnSamples: false,
    fexpSamples: false,
    testingCategory: Number(getUrlParameter("testing_category_id")) || null,
    sample_id: this.props.initialSampleId,
    clientSuiteId: null,
    requiresNotificationAuthorisation: false,
    awaitingNotification: false,
    analysis_started_more_than_24_hrs_since_time_sampled: false,
    analysis_started_less_than_24_hrs_since_time_sampled: false,
    requested_turnaround: null,
    hasProtocols: false,
  };

  formKey = "sample_reporting/sample_search";

  getSearchParams = () => {
    const s = this.state;
    const p = this.props;
    return {
      company_id: s.company_id || null,
      product_name: s.product_name || "",
      start_date: s.start_date ? s.start_date.format("YYYY-MM-DD") : null,
      end_date: s.end_date ? s.end_date.format("YYYY-MM-DD") : null,
      company_branch_id: s.company_branch_id || null,
      protocol_id: s.protocol_id || null,
      received: s.received || "all",
      reviewed: s.reviewed || "all",
      invoice_status: s.invoice_status || "all",
      contractReviewed: s.contractReviewed || "all",
      matrix_id: s.matrixId || null,
      technique_ids: s.techniqueIds || [],
      data_field_params: this.dataFieldFilter ? this.dataFieldFilter.queryParams() : [],
      found: s.found || false,
      exceed: s.exceededLimits || false,
      outstanding_todos: s.outstandingTodos || false,
      show_withdrawn_samples: s.withdrawnSamples || false,
      testing_category_id: s.testingCategory || null,
      sample_ids: s.sample_id ? [s.sample_id] : null,
      date_filter: s.dateFilter || "registered",
      client_suite_id: s.clientSuiteId || null,
      with_invoice_sent_sample_ledger_items: p.withInvoiceSentSampleLedgerItems || false,
      with_unallocated_sample_ledger_items: p.withUnallocatedSampleCharges || false,
      requires_notification_authorisation: s.requiresNotificationAuthorisation || false,
      awaiting_notification: s.awaitingNotification || false,
      analysis_started_more_than_24_hrs_since_time_sampled: s.analysis_started_more_than_24_hrs_since_time_sampled || false,
      analysis_started_less_than_24_hrs_since_time_sampled: s.analysis_started_less_than_24_hrs_since_time_sampled || false,
      laboratory_company_id: p.laboratoryCompanyId || null,
      fexp: s.fexpSamples || false,
      requested_turnaround: s.requested_turnaround != null ? s.requested_turnaround : null,
    };
  };

  applyTemplate = (tpl) => {
    const next = {};
    if ("company_id" in tpl) next.company_id = tpl.company_id || null;
    if ("product_name" in tpl) next.product_name = tpl.product_name || "";
    if ("start_date" in tpl) next.start_date = tpl.start_date ? moment(tpl.start_date) : null;
    if ("end_date" in tpl) next.end_date = tpl.end_date ? moment(tpl.end_date) : null;
    if ("company_branch_id" in tpl) next.company_branch_id = tpl.company_branch_id || null;
    if ("protocol_id" in tpl) next.protocol_id = tpl.protocol_id || null;
    if ("received" in tpl) next.received = tpl.received || "all";
    if ("reviewed" in tpl) next.reviewed = tpl.reviewed || "all";
    if ("invoice_status" in tpl) next.invoice_status = tpl.invoice_status || "all";
    if ("contractReviewed" in tpl) next.contractReviewed = tpl.contractReviewed || "all";
    if ("matrix_id" in tpl) next.matrixId = tpl.matrix_id || null;
    if ("technique_ids" in tpl) next.techniqueIds = Array.isArray(tpl.technique_ids) ? tpl.technique_ids : [];
    if ("found" in tpl) next.found = !!tpl.found;
    if ("exceed" in tpl) next.exceededLimits = !!tpl.exceed;
    if ("outstanding_todos" in tpl) next.outstandingTodos = !!tpl.outstanding_todos;
    if ("show_withdrawn_samples" in tpl) next.withdrawnSamples = !!tpl.show_withdrawn_samples;
    if ("testing_category_id" in tpl) next.testingCategory = tpl.testing_category_id || null;
    if ("sample_ids" in tpl) next.sample_id = Array.isArray(tpl.sample_ids) ? tpl.sample_ids[0] : null;
    if ("date_filter" in tpl) next.dateFilter = tpl.date_filter || "registered";
    if ("client_suite_id" in tpl) next.clientSuiteId = tpl.client_suite_id || null;
    if ("requires_notification_authorisation" in tpl) next.requiresNotificationAuthorisation = !!tpl.requires_notification_authorisation;
    if ("awaiting_notification" in tpl) next.awaitingNotification = !!tpl.awaiting_notification;
    if ("analysis_started_more_than_24_hrs_since_time_sampled" in tpl)
      next.analysis_started_more_than_24_hrs_since_time_sampled = !!tpl.analysis_started_more_than_24_hrs_since_time_sampled;
    if ("analysis_started_less_than_24_hrs_since_time_sampled" in tpl)
      next.analysis_started_less_than_24_hrs_since_time_sampled = !!tpl.analysis_started_less_than_24_hrs_since_time_sampled;
    if ("fexp" in tpl) next.fexpSamples = !!tpl.fexp;
    if ("requested_turnaround" in tpl) next.requested_turnaround = tpl.requested_turnaround != null ? Number(tpl.requested_turnaround) : null;
    this.setState(next);
  };

  canSubmit = (): boolean => {
    const { fixedSearchParameters } = this.props;
    const { start_date, end_date } = this.state;
    if ((!start_date || !end_date) && !fixedSearchParameters.job_id) return false;
    return true;
  };

  findSamples = () => {
    const {
      onSearch,
      withUnallocatedSampleCharges,
      withInvoiceSentSampleLedgerItems,
      laboratoryCompanyId,
      fixedSearchParameters,
    } = this.props;
    const {
      company_id,
      product_name,
      start_date,
      end_date,
      company_branch_id,
      protocol_id,
      received,
      reviewed,
      invoice_status,
      contractReviewed,
      matrixId,
      techniqueIds,
      found,
      exceededLimits,
      outstandingTodos,
      withdrawnSamples,
      fexpSamples,
      testingCategory,
      sample_id,
      dateFilter,
      clientSuiteId,
      requiresNotificationAuthorisation,
      awaitingNotification,
      analysis_started_more_than_24_hrs_since_time_sampled,
      analysis_started_less_than_24_hrs_since_time_sampled,
      requested_turnaround
    } = this.state;
    this.setState({ loading: true });
    onSearch(null);
    fetchSamples({
      company_id,
      product_name,
      start_date,
      end_date,
      company_branch_id,
      protocol_id,
      received,
      reviewed,
      invoice_status,
      contractReviewed,
      matrix_id: matrixId,
      technique_ids: techniqueIds,
      data_field_params: this.dataFieldFilter ? this.dataFieldFilter.queryParams() : [],
      found,
      exceed: exceededLimits,
      outstanding_todos: outstandingTodos,
      show_withdrawn_samples: withdrawnSamples,
      testing_category_id: testingCategory,
      sample_ids: sample_id ? [sample_id] : null,
      date_filter: dateFilter,
      client_suite_id: clientSuiteId,
      with_invoice_sent_sample_ledger_items: withInvoiceSentSampleLedgerItems,
      with_unallocated_sample_ledger_items: withUnallocatedSampleCharges,
      requires_notification_authorisation: requiresNotificationAuthorisation,
      awaiting_notification: awaitingNotification,
      analysis_started_more_than_24_hrs_since_time_sampled,
      analysis_started_less_than_24_hrs_since_time_sampled,
      laboratory_company_id: laboratoryCompanyId,
      fexp: fexpSamples,
      requested_turnaround,
      ...fixedSearchParameters,
    })
      .then((sample_list) => {
        this.setState({ loading: false });
        onSearch(sample_list);
      })
      .catch((e) => {
        this.setState({ loading: false });
      });
  };

  branchChanged = (branch: CompanyBranch) => {
    this.setState({ company_branch_id: branch ? branch.id : undefined });
  };

  protocolChanged = (protocol) => {
    this.setState({ protocol_id: protocol ? protocol.id : undefined });
  };

  protocolsLoaded = (protocols) => {
    this.setState((prevState) => {
      const hasProtocols = protocols.length > 0;
      const protocolExists =
        hasProtocols && prevState.protocol_id != null
          ? protocols.some((p) => p.id === prevState.protocol_id)
          : false;
      return {
        hasProtocols,
        protocol_id: hasProtocols && protocolExists ? prevState.protocol_id : undefined,
      };
    });
  };

  componentDidMount() {
    if (getUrlParameter("run") || this.props.initialSampleId || this.props.runSearch) {
      this.findSamples();
    }
  }

  render() {
    const {
      canFindCompany,
      clientView,
      showReceived,
      showReviewed,
      showContractReviewed,
      showCheckboxes,
      hideCompanySearch,
      withInvoiceSentSampleLedgerItems,
    } = this.props;
    const {
      company_id,
      loading,
      product_name,
      start_date,
      end_date,
      startDateFocused,
      endDateFocused,
      company_branch_id,
      protocol_id,
      received,
      invoice_status,
      found,
      exceededLimits,
      outstandingTodos,
      withdrawnSamples,
      fexpSamples,
      testingCategory,
      sample_id,
      dateFilter,
      clientSuiteId,
      reviewed,
      contractReviewed,
      requiresNotificationAuthorisation,
      awaitingNotification,
      analysis_started_more_than_24_hrs_since_time_sampled,
      analysis_started_less_than_24_hrs_since_time_sampled,
      requested_turnaround,
      matrixId,
      techniqueIds,
      hasProtocols
    } = this.state;
    const receivedOptions = [
      { label: I18n.t("show_all"), value: "all" },
      { label: I18n.t("not_received"), value: "not_received" },
      { label: I18n.t("received"), value: "received" },
    ];
    const reviewedOptions = [
      { label: I18n.t("show_all"), value: "all" },
      { label: I18n.t("reviewed"), value: "reviewed" },
      { label: I18n.t("not_reviewed"), value: "not_reviewed" },
    ];
    const contractReviewedOptions = reviewedOptions;
    return (
      <Container>
        <Row>
          <Column>
            <label>{I18n.t("testing_category")}</label>
            <TestingCategorySelect
              value={testingCategory}
              onChange={(o) => this.setState({ testingCategory: o.value })}
            />
          </Column>
          <Column>
            <DateFilter
              filterByInvoiceSentDate={withInvoiceSentSampleLedgerItems}
              clientView={clientView}
              setDateFilter={(dateFilter) => this.setState({ dateFilter })}
              dateFilter={dateFilter}
              start_date={start_date}
              end_date={end_date}
              onDatesChange={({ start_date, end_date }) =>
                this.setState({ start_date, end_date })
              }
            />
          </Column>
          <Column>
            <label>{I18n.t("sample_id")}</label>
            <Input
              focus
              onPressEnter={this.findSamples}
              className="form-control"
              value={sample_id}
              onChange={(e) => this.setState({ sample_id: e.target.value })}
            />
          </Column>
          {canFindCompany &&
            !hideCompanySearch && (
              <Column>
                <CompanyFilter
                  company_id={company_id}
                  onChange={(company_id) => this.setState({ company_id, clientSuiteId: null, protocol_id: null })}
                />
              </Column>
            )}
          {company_id && (
            <Column>
              <CompanyBranchFilter
                key={company_id}
                company_id={company_id}
                onChange={this.branchChanged}
                company_branch_id={company_branch_id}
              />
            </Column>
          )}
          {company_id && (
            <Column style={{ display: hasProtocols ? undefined : "none" }}>
              <ProtocolFilter
                key={company_id}
                company_id={company_id}
                onChange={this.protocolChanged}
                protocol_id={protocol_id}
                onProtocolsLoaded={this.protocolsLoaded}
              />
            </Column>
          )}
          {showReceived && (
            <Column>
              <label>{I18n.t("received_status")}</label>
              <br />
              <DropDown
                onChange={(dropDownItem) => this.setState({ received: dropDownItem.value })}
                options={receivedOptions}
                value={received}
              />
            </Column>
          )}
          {showReviewed && (
            <Column>
              <label>{I18n.t("reviewed_status")}</label>
              <br />
              <DropDown
                onChange={(dropDownItem) => this.setState({ reviewed: dropDownItem.value })}
                options={reviewedOptions}
                value={reviewed}
              />
            </Column>
          )}
          {!clientView && (
            <Column>
              <label>{I18n.t("invoice_status.label")}</label>
              <br />
              <InvoiceStatus
                onChange={(value) => this.setState({ invoice_status: value })}
                value={invoice_status}
              />
            </Column>
          )}
          {!clientView &&
            showContractReviewed && (
              <Column>
                <label>{I18n.t("contract_reviewed_status")}</label>
                <br />
                <DropDown
                  onChange={(dropDownItem) =>
                    this.setState({ contractReviewed: dropDownItem.value })
                  }
                  options={contractReviewedOptions}
                  value={contractReviewed}
                />
              </Column>
            )}
          <Column>
            <label>{I18n.t("product_name")}</label>
            <Input
              focus
              onPressEnter={this.findSamples}
              className="form-control"
              value={product_name}
              onChange={(e) => this.setState({ product_name: e.target.value })}
            />
          </Column>
          <Column>
            <label>{I18n.t("matrix")}</label>
            <MatrixInput
              multiple={false}
              value={matrixId ? [matrixId] : []}
              onChange={(option) => {
                if (Array.isArray(option)) {
                  this.setState({ matrixId: option.length > 0 ? option[0].value : null });
                } else {
                  this.setState({ matrixId: option ? option.value : null });
                }
              }}
              defaultStyle
            />
          </Column>
          <Column>
            <label>{I18n.t("technique")}</label>
            <TechniqueInput
              multiple
              value={techniqueIds}
              returnIds
              onChange={(ids) => {
                this.setState({ techniqueIds: Array.isArray(ids) ? ids : (ids ? [ids] : []) });
              }}
              defaultStyle
            />
          </Column>
          {!clientView && (
            <Column>
              <label>{I18n.t("requested_turnaround")}</label>
              <DropDown
                onChange={(dropDownItem) => 
                  this.setState({ requested_turnaround: dropDownItem ? dropDownItem.value : null })
                }
                options={[
                  { label: I18n.t("show_all"), value: null },
                  ...Array.from({ length: 21 }, (_, i) => ({ 
                    label: i.toString(), 
                    value: i 
                  }))
                ]}
                value={requested_turnaround}
              />
            </Column>
          )}
          {company_id && (
            <Column>
              <label>{I18n.t("client_suite")}</label>
              <ClientSuiteInput
                company_id={company_id}
                multiple={false}
                value={clientSuiteId}
                onChange={(option) =>
                  this.setState({ clientSuiteId: option ? option.value : null })
                }
              />
            </Column>
          )}
        </Row>
        {showCheckboxes && (
          <Row>
            <FullColumn style={{ marginTop: 10 }}>
              <label style={{ marginRight: 40 }}>
                <ResultDetectedInput
                  value={found}
                  onChange={(isFound) => this.setState({ found: isFound })}
                />
                &nbsp; {I18n.t("with_detected_results")}
              </label>
              <label style={{ marginRight: 40 }}>
                <ExceededLimitsInput
                  value={exceededLimits}
                  onChange={(val) => this.setState({ exceededLimits: val })}
                />
                &nbsp; {I18n.t("with_results_exceeding_limits")}
                &nbsp;
                <span
                  class="badge"
                  data-toggle="tooltip"
                  data-placement="top"
                  title="For Pesticide samples, only EU MRL limits are used to flag failures on this page"
                >
                  <i className="fa fa-info" />
                </span>
              </label>
              <label style={{ marginRight: 40 }}>
                <OutstandingTodosInput
                  value={outstandingTodos}
                  onChange={(val) => this.setState({ outstandingTodos: val })}
                />
                &nbsp; {I18n.t("with_outstanding_todos")}
              </label>
              <label style={{ marginRight: 40 }}>
                <WithdrawnSamplesInput
                  value={withdrawnSamples}
                  onChange={(val) => this.setState({ withdrawnSamples: val })}
                />
                &nbsp; {I18n.t("show_withdrawn_samples")}
              </label>
              <label style={{ marginRight: 40 }}>
                <WithdrawnSamplesInput
                  value={fexpSamples}
                  onChange={(val) => this.setState({ fexpSamples: val })}
                />
                &nbsp; {I18n.t("show_fexp_samples")}
              </label>
              {!clientView && (
                <label style={{ marginRight: 40 }}>
                  <RequiresNotificationAuthorisationInput
                    value={requiresNotificationAuthorisation}
                    onChange={(val) =>
                      this.setState({ requiresNotificationAuthorisation: val })
                    }
                  />
                  &nbsp; {I18n.t("requires_notification_authorisation")}
                </label>
              )}
              {!clientView && (
                <label style={{ marginRight: 40 }}>
                  <RequiresNotificationAuthorisationInput
                    value={awaitingNotification}
                    onChange={(val) => this.setState({ awaitingNotification: val })}
                  />
                  &nbsp; {I18n.t("awaiting_notification")}
                </label>
              )}
              {!clientView && (
                <label style={{ marginRight: 20, color: "green" }}>
                  <RequiresNotificationAuthorisationInput
                    value={analysis_started_more_than_24_hrs_since_time_sampled}
                    onChange={(val) =>
                      this.setState({
                        analysis_started_more_than_24_hrs_since_time_sampled: val,
                      })
                    }
                  />
                  &nbsp; {I18n.t("analysis_started_more_than_24h_since_time_sampled")}
                </label>
              )}
              {!clientView && (
                <label style={{ marginRight: 0, color: "blue" }}>
                  <RequiresNotificationAuthorisationInput
                    value={analysis_started_less_than_24_hrs_since_time_sampled}
                    onChange={(val) =>
                      this.setState({
                        analysis_started_less_than_24_hrs_since_time_sampled: val,
                      })
                    }
                  />
                  &nbsp; {I18n.t("analysis_started_less_than_24h_since_time_sampled")}
                </label>
              )}
            </FullColumn>
          </Row>
        )}
        {company_id && (
          <Row style={{ paddingBottom: 0, paddingTop: 5 }}>
            <FullColumn>
              <DataFieldFilter
                ref={(ref) => (this.dataFieldFilter = ref)}
                onSubmit={this.findSamples}
                company_id={company_id}
                company_branch_id={company_branch_id}
              />
            </FullColumn>
          </Row>
        )}
        <Row style={{ paddingBottom: 0, paddingTop: 5 }}>
          <Column style={{ marginBottom: 0, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Button
              disabled={!this.canSubmit()}
              title={loading ? I18n.t("loading") : I18n.t("find_samples")}
              icon={loading ? "fa-spin fa-spinner" : "fa-search"}
              onClick={this.findSamples}
            />
            {!clientView && (
              <SearchTemplateManager
                formKey={this.formKey}
                getCurrentParams={this.getSearchParams}
                applyTemplate={this.applyTemplate}
                isClientView={clientView}
              />
            )}
          </Column>
        </Row>
      </Container>
    );
  }
}
